/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// WCAG 2.1 — keyboard scrolling for scrollable regions with tabindex="0"
document.addEventListener('keydown', function(event) {
  var el = document.activeElement;
  if (!el || el.tagName.toLowerCase() !== 'scrollable-content') return;
  if (event.key === 'ArrowLeft') {
    el.scrollBy({ left: -120, behavior: 'smooth' });
    event.preventDefault();
  } else if (event.key === 'ArrowRight') {
    el.scrollBy({ left: 120, behavior: 'smooth' });
    event.preventDefault();
  } else if (event.key === 'ArrowUp') {
    el.scrollBy({ top: -120, behavior: 'smooth' });
    event.preventDefault();
  } else if (event.key === 'ArrowDown') {
    el.scrollBy({ top: 120, behavior: 'smooth' });
    event.preventDefault();
  }
});

// Cookie Information consent popup trigger
// Use <a href="#" class="cookie-consent-trigger">...</a> anywhere in page content
document.addEventListener('click', function(event) {
  var trigger = event.target.closest('.cookie-consent-trigger');
  if (!trigger) return;
  event.preventDefault();
  if (window.CookieInformation && typeof window.CookieInformation._restartBanner === 'function') {
    window.CookieInformation._restartBanner();
  }
});

// Advent bundle: clears a locked bundle from the cart page/drawer itself.
// Use <button class="js-advent-cart-reset">...</button> anywhere in page content
document.addEventListener('click', function(event) {
  var trigger = event.target.closest('.js-advent-cart-reset');
  if (!trigger) return;
  event.preventDefault();
  fetch(window.Shopify.routes.root + 'cart/clear.json', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  .then(function() {
    return fetch(window.Shopify.routes.root + 'cart/update.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'advent-bundle': '' } }),
    });
  })
  .then(function() {
    var redirect = trigger.getAttribute('href');
    if (redirect && redirect !== '#') {
      location.href = redirect;
    } else {
      location.reload();
    }
  });
});

// Facet filtering (collection page "Filtre") aborts the in-flight request whenever another
// filter is picked before the previous one resolves — normal when selecting multiple sizes or
// colors in quick succession. Focal's ProductFacet doesn't catch that abort itself, so it
// surfaces as an uncaught "signal is aborted without reason" error. The abort is expected and
// harmless, so we swallow it here rather than upstream in theme.js.
window.addEventListener('unhandledrejection', function(event) {
  if (event.reason && event.reason.name === 'AbortError') {
    event.preventDefault();
  }
});

// PDP image zoom: tap/click a product image to open the stacked-image dialog (snippets/lakor-image-zoom.liquid).
// Desktop: click an image inside the dialog to zoom in, move the mouse to pan.
(function() {
  var downX = 0;
  var downY = 0;
  var desktopZoom = window.matchMedia('(min-width: 1000px) and (hover: hover)');

  document.addEventListener('pointerdown', function(event) {
    downX = event.clientX;
    downY = event.clientY;
  });

  document.addEventListener('click', function(event) {
    var item = event.target.closest('.product__media--zoomable .product__media-item[data-media-type="image"]');
    if (item) {
      // Ignore the click that ends a carousel drag
      if (Math.abs(event.clientX - downX) > 8 || Math.abs(event.clientY - downY) > 8) return;
      openZoom(item.closest('product-media'));
      return;
    }

    var dialog = event.target.closest('dialog.lakor-zoom');
    if (!dialog) return;

    if (event.target.closest('[data-lakor-zoom-close]') || event.target === dialog) {
      dialog.close();
      return;
    }

    var figure = event.target.closest('.lakor-zoom__item');
    if (figure && desktopZoom.matches) {
      var zoomed = figure.classList.toggle('is-zoomed');
      if (zoomed) {
        figure.querySelector('img').sizes = '2600px';
        setZoomOrigin(figure, event);
      }
    }
  });

  document.addEventListener('mousemove', function(event) {
    var figure = event.target.closest && event.target.closest('.lakor-zoom__item.is-zoomed');
    if (figure) setZoomOrigin(figure, event);
  });

  function setZoomOrigin(figure, event) {
    var rect = figure.getBoundingClientRect();
    figure.style.setProperty('--zoom-x', ((event.clientX - rect.left) / rect.width * 100) + '%');
    figure.style.setProperty('--zoom-y', ((event.clientY - rect.top) / rect.height * 100) + '%');
  }

  function updateProgress(dialog) {
    var scroller = dialog.querySelector('.lakor-zoom__scroll');
    var progress = dialog.querySelector('.lakor-zoom__progress');
    var figures = Array.prototype.filter.call(dialog.querySelectorAll('.lakor-zoom__item'), function(figure) { return !figure.hidden; });
    if (!progress || !figures.length) return;

    var mid = scroller.getBoundingClientRect().top + scroller.clientHeight / 2;
    var current = 0;
    figures.forEach(function(figure, index) {
      if (figure.getBoundingClientRect().top <= mid) current = index;
    });

    var atEnd = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4;
    if (atEnd) current = figures.length - 1;

    progress.hidden = figures.length < 2;
    progress.classList.toggle('is-end', atEnd);
    progress.querySelector('[data-lakor-zoom-count]').textContent = (current + 1) + ' / ' + figures.length;
  }

  document.addEventListener('scroll', function(event) {
    var dialog = event.target.closest && event.target.closest('dialog.lakor-zoom');
    if (dialog) updateProgress(dialog);
  }, true);

  function openZoom(productMedia) {
    var dialog = productMedia.querySelector('dialog.lakor-zoom');
    var selected = productMedia.querySelector('.product__media-item.is-selected');
    var selectedId = selected && selected.getAttribute('data-media-id');

    dialog.querySelectorAll('.lakor-zoom__item').forEach(function(figure) {
      var source = productMedia.querySelector('.product__media-item[data-media-id="' + figure.getAttribute('data-media-id') + '"]');
      figure.hidden = !source || source.classList.contains('is-filtered');
      figure.classList.remove('is-zoomed');
    });

    dialog.showModal();

    var target = dialog.querySelector('.lakor-zoom__item[data-media-id="' + selectedId + '"]');
    if (target && !target.hidden) {
      target.scrollIntoView({ block: 'start' });
    }
    updateProgress(dialog);
  }
})();

// PDP carousel: pinch-to-zoom and swipe must not fire together. Flickity tracks the first finger as a swipe, so
// when a second finger lands (or the page is zoomed) we cancel the swipe and disable dragging until the pinch
// is over and the page is back at normal scale, otherwise releasing the pinch slides to the next image.
(function() {
  var locked = null; // { carousel, draggable }

  function zoomed() {
    return window.visualViewport && window.visualViewport.scale > 1.01;
  }

  async function lock(carousel) {
    if (locked) return;
    var flickity = await carousel.flickity;
    locked = { carousel: carousel, draggable: flickity.options.draggable };
    carousel.setDraggable(false);
    flickity._pointerDone();
    flickity.isDragging = false;
    flickity.viewport.classList.remove('is-pointer-down', 'is-dragging');
    flickity.positionSliderAtSelected();
  }

  function unlock() {
    if (!locked) return;
    locked.carousel.setDraggable(locked.draggable);
    locked = null;
  }

  document.addEventListener('touchstart', function(event) {
    if (event.touches.length < 2) return;
    var carousel = event.target.closest && event.target.closest('flickity-carousel');
    if (carousel && carousel.closest('product-media')) lock(carousel);
  }, { capture: true, passive: true });

  function maybeUnlock(event) {
    if (event.touches && event.touches.length > 0) return;
    // Let the browser finish its pinch/zoom settling before dragging is allowed again
    setTimeout(function() { if (!zoomed()) unlock(); }, 300);
  }

  document.addEventListener('touchend', maybeUnlock, { passive: true });
  document.addEventListener('touchcancel', maybeUnlock, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', function() {
      if (locked && !zoomed()) setTimeout(function() { if (!zoomed()) unlock(); }, 300);
    });
  }
})();

// Sharper images while pinch-zoomed. The carousel and the zoom dialog start with the same size hint (so the
// dialog reuses the file the carousel already downloaded). When the shopper actually pinch-zooms the page, the
// images currently in view are upgraded to a 2x-screen-width source so there are real pixels to look at.
(function() {
  var vv = window.visualViewport;
  if (!vv) return;
  var pending = false;

  function upgradeVisibleImages() {
    pending = false;
    if (vv.scale <= 1.01) return;
    var dialog = document.querySelector('dialog.lakor-zoom[open]');
    var images = dialog
      ? dialog.querySelectorAll('.lakor-zoom__item:not([hidden]) img')
      : document.querySelectorAll('.product__media-item.is-selected img');

    images.forEach(function(img) {
      var rect = img.getBoundingClientRect();
      if (rect.bottom < vv.offsetTop || rect.top > vv.offsetTop + vv.height || img.dataset.sharp) return;
      img.dataset.sharp = '1';
      img.sizes = '200vw';
    });
  }

  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(upgradeVisibleImages);
  }

  vv.addEventListener('resize', schedule);
  vv.addEventListener('scroll', schedule);
})();

// Sticky add to cart on the PDP. Shown whenever the main CTA is out of view (above or below the viewport),
// replacing the theme's "only after the CTA has been scrolled past" logic. Products with several sizes get a
// "Vælg størrelse" button that scrolls the main CTA into view; single-size products add to cart directly.
(function() {
  var sticky = document.querySelector('product-sticky-form');
  var cta = document.getElementById('MainPaymentContainer');
  if (!sticky || !cta) return;

  // Drop the theme's own observer and use ours instead
  if (sticky.intersectionObserver) sticky.intersectionObserver.disconnect();

  var footer = document.querySelector('.shopify-section--footer');
  var ctaVisible = false;
  var footerVisible = false;

  var styles = getComputedStyle(document.documentElement);
  var headerOffset = (parseInt(styles.getPropertyValue('--header-height')) || 0) * (parseInt(styles.getPropertyValue('--enable-sticky-header')) || 0)
    + (parseInt(styles.getPropertyValue('--announcement-bar-height')) || 0) * (parseInt(styles.getPropertyValue('--enable-sticky-announcement-bar')) || 0);

  var observer = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      if (entry.target === cta) ctaVisible = entry.isIntersecting;
      if (entry.target === footer) footerVisible = entry.isIntersecting;
    });
    var pocket = window.matchMedia(window.themeVariables.breakpoints.pocket).matches;
    sticky.hidden = ctaVisible || (pocket && footerVisible);
  }, { rootMargin: '-' + headerOffset + 'px 0px 0px 0px' });

  observer.observe(cta);
  if (footer) observer.observe(footer);
  sticky.intersectionObserver = observer;

  sticky.addEventListener('click', function(event) {
    if (!event.target.closest('[data-sticky-scroll-to-cta]')) return;
    cta.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
})();

/**
 * Search drawer
 * - Open the keyboard in the same tap that opens the drawer. The theme focuses the field 150ms later, which iOS ignores
 *   (the keyboard only opens from inside a user gesture) and the drawer is still hidden during the tap, so users had to
 *   tap the field a second time. A throwaway input takes focus during the tap and hands it to the real field.
 * - Wait for 3 characters before running predictive search.
 */
(function() {
  document.addEventListener('click', function(event) {
    if (!event.target.closest('[aria-controls="search-drawer"]')) return;

    var proxy = document.createElement('input');
    proxy.type = 'text';
    proxy.setAttribute('aria-hidden', 'true');
    proxy.tabIndex = -1;
    proxy.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;font-size:16px;';
    document.body.appendChild(proxy);
    proxy.focus({ preventScroll: true });

    var remove = function() { proxy.remove(); };
    document.addEventListener('focusin', function onFocusIn(e) {
      if (e.target === proxy) return;
      document.removeEventListener('focusin', onFocusIn);
      remove();
    });
    setTimeout(remove, 1000);
  });

  // Capture phase, so this runs before the theme's delegated input listener on the drawer
  document.addEventListener('input', function(event) {
    var input = event.target;
    if (input.name !== 'q' || !input.closest('#search-drawer')) return;

    var length = input.value.trim().length;
    if (length === 0 || length >= 3) return;

    event.stopImmediatePropagation();

    var drawer = input.closest('predictive-search-drawer');
    if (drawer.abortController) drawer.abortController.abort();
    drawer.drawerContentElement.classList.remove('drawer__content--center');
    drawer.drawerFooterElement.hidden = true;
    drawer.loadingStateElement.hidden = true;
    drawer.resultsElement.hidden = true;
    if (drawer.menuListElement) drawer.menuListElement.hidden = false;
  }, true);
})();

/**
 * Search aliases, a replacement for the retired Shopify search synonyms
 * Maps what people type to a word the products contain (metaobject "Search alias", see snippets/lakor-search-aliases.liquid).
 * - Form submits are rewritten before they leave the page, so "kasket" goes straight to /search?q=cap&alias_of=kasket
 * - Direct visits and links to /search?q=kasket are redirected once
 * - The results page shows what the customer typed, and sends it to GTM since the search report only sees the replacement
 * - The predictive search dropdown searches for the replacement too (and also reports to GTM, source 'predictive')
 */
(function() {
  var aliases = Object.assign(Object.create(null), window.LakorSearchAliases);
  var searchPath = /\/search\/?$/;
  var normalize = function(query) { return query.trim().toLowerCase().replace(/\s+/g, ' '); };
  var params = new URLSearchParams(location.search);
  var original = params.get('alias_of');

  var trackAlias = function(typed, used, source) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'search_alias', search_term_original: typed, search_term_used: used, source: source });
  };

  if (searchPath.test(location.pathname) && !original && aliases[normalize(params.get('q') || '')]) {
    params.set('alias_of', params.get('q').trim());
    params.set('q', aliases[normalize(params.get('q'))]);
    location.replace(location.pathname + '?' + params);
    return;
  }

  document.addEventListener('submit', function(event) {
    var form = event.target;
    if (!searchPath.test(new URL(form.action, location.href).pathname) || !form.elements.q) return;

    var target = aliases[normalize(form.elements.q.value)];
    if (!target) return;

    event.preventDefault();
    var data = new URLSearchParams(new FormData(form));
    data.set('alias_of', form.elements.q.value.trim());
    data.set('q', target);
    location.href = form.action + '?' + data;
  });

  if (searchPath.test(location.pathname) && original) {
    var note = document.querySelector('[data-alias-note]');
    if (note) {
      note.textContent = note.dataset.aliasNote.replace('[[original]]', function() { return original; });
      note.hidden = false;
    }
    document.querySelectorAll('.main-search__input').forEach(function(input) { input.value = original; });

    trackAlias(original, params.get('q'), 'results');
  }

  // Predictive search dropdown: search for the replacement word. The "View all results" button submits a form, which is rewritten above.
  var drawerPrototype = (window.customElements.get('predictive-search-drawer') || {}).prototype;
  var lastTracked;

  ['_doPredictiveSearch', '_doLiquidSearch'].forEach(function(method) {
    var originalMethod = drawerPrototype && drawerPrototype[method];
    if (!originalMethod) return;

    drawerPrototype[method] = function(term) {
      var target = aliases[normalize(term)];
      if (target && lastTracked !== term) {
        lastTracked = term;
        trackAlias(term.trim(), target, 'predictive');
      }
      return originalMethod.call(this, target || term);
    };
  });
})();
